/**
 * Loan amortization (spec §12). Monthly-accurate — annual approximations
 * misstate payoff dates and interest by enough to matter.
 */

/** Standard fixed payment for a fully-amortizing loan. */
export function monthlyPaymentFor(principal: number, annualRate: number, termMonths: number): number {
  if (principal <= 0 || termMonths <= 0) return 0;
  const r = annualRate / 12;
  if (r === 0) return principal / termMonths;
  return (principal * r) / (1 - Math.pow(1 + r, -termMonths));
}

export interface AmortSummary {
  /** Months until the balance reaches zero; null when the payment never covers interest. */
  payoffMonths: number | null;
  totalInterest: number;
}

/** Run the loan to payoff (or give up when the payment can't cover interest). */
export function amortize(balance: number, annualRate: number, monthlyPayment: number): AmortSummary {
  if (balance <= 0) return { payoffMonths: 0, totalInterest: 0 };
  const r = annualRate / 12;
  if (monthlyPayment <= balance * r) return { payoffMonths: null, totalInterest: Infinity };

  let bal = balance;
  let months = 0;
  let interest = 0;
  // Hard ceiling far beyond any real mortgage, in case of pathological input.
  while (bal > 0.005 && months < 1200) {
    const i = bal * r;
    interest += i;
    bal = Math.max(0, bal - (monthlyPayment - i));
    months += 1;
  }
  return { payoffMonths: months, totalInterest: interest };
}

export interface YearAmortResult {
  endBalance: number;
  interestPaid: number;
  principalPaid: number;
  /** Actual cash paid this year (stops when the loan hits zero). */
  totalPaid: number;
}

/** Advance one year (12 monthly payments) of a loan. */
export function amortizeYear(balance: number, annualRate: number, monthlyPayment: number): YearAmortResult {
  let bal = balance;
  let interestPaid = 0;
  let principalPaid = 0;
  let totalPaid = 0;
  const r = annualRate / 12;
  for (let m = 0; m < 12 && bal > 0.005; m++) {
    const i = bal * r;
    const principal = Math.min(bal, Math.max(0, monthlyPayment - i));
    const paid = Math.min(monthlyPayment, bal + i);
    interestPaid += i;
    principalPaid += principal;
    totalPaid += paid;
    bal = Math.max(0, bal - principal);
  }
  return { endBalance: bal, interestPaid, principalPaid, totalPaid };
}
